/**
 * What a model call was for (Miles, 2026-10-09, the ⓘ's cost receipt: "Total + plain breakdown" — each line says in
 * plain words what was paid for).
 *
 * The server stamps every model call with one word from a closed set where the call is made
 * (functions/src/lib/generationUsage.ts USAGE_PURPOSES); this is its client twin, pinned byte for byte by
 * tests/tools/usagePurpose.test.ts — one owner, one twin, never a third copy. The receipt's plain words for each live
 * with the receipt (usageReceiptLines.ts, in the on-demand `live-steps` chunk).
 *
 * And the ONE way a usage row's facts are copied (`usageRowFacts`): every mapper on every lane — the live `done`
 * event, a resumed task, a reload — spreads it, so none can drop a fact the ⓘ needs. Two resume paths once dropped
 * the lane flag, the stage and the cost source (2026-09-08); a field-by-field copy is how that happens.
 *
 * Import-free on purpose: it rides the chat engine and every mapper reads it.
 */

export const USAGE_PURPOSES = Object.freeze(['route', 'catch-up', 'read', 'plan', 'answer', 'file', 'tool'] as const);
export type UsagePurpose = (typeof USAGE_PURPOSES)[number];
const USAGE_PURPOSE_SET: ReadonlySet<string> = new Set(USAGE_PURPOSES);

export function isUsagePurpose(value: unknown): value is UsagePurpose {
  return typeof value === 'string' && USAGE_PURPOSE_SET.has(value);
}

/** An icon word's shape (the closed set itself rides the live-steps chunk; an unknown word draws its kind's icon). */
const ICON_WORD_RE = /^[a-z]{2,16}$/;

export interface UsageRowFacts {
  billedSeparately?: true;
  sidecarKey?: string;
  stage?: string;
  costSource?: 'provider' | 'estimate';
  effort?: string;
  purpose?: UsagePurpose;
  icon?: string;
  responseTimeMs?: number;
}

/**
 * The facts a usage row carries beside its figures — each closed or shape-checked, each OMITTED when absent (never
 * `undefined`, so a Firestore write stays valid). `durationMs` (the server's name) is read as `responseTimeMs`.
 */
export function usageRowFacts(row: unknown): UsageRowFacts {
  if (!row || typeof row !== 'object') return {};
  const u = row as Record<string, unknown>;
  const out: UsageRowFacts = {};
  if (u.billedSeparately === true) out.billedSeparately = true;
  if (typeof u.sidecarKey === 'string' && u.sidecarKey.trim()) out.sidecarKey = u.sidecarKey;
  if (typeof u.stage === 'string' && u.stage.trim()) out.stage = u.stage;
  if (u.costSource === 'provider' || u.costSource === 'estimate') out.costSource = u.costSource;
  if (typeof u.effort === 'string' && u.effort.trim()) out.effort = u.effort;
  if (isUsagePurpose(u.purpose)) {
    out.purpose = u.purpose;
    if (u.purpose === 'tool' && typeof u.icon === 'string' && ICON_WORD_RE.test(u.icon)) out.icon = u.icon;
  }
  const ms = typeof u.responseTimeMs === 'number' ? u.responseTimeMs : u.durationMs;
  if (typeof ms === 'number' && Number.isFinite(ms) && ms > 0) out.responseTimeMs = ms;
  return out;
}
